import { type ReactNode, useMemo, useState } from "react";

import type {
  ChannelEnvironmentFormValues,
  ChannelEnvironmentProfile,
} from "../useChannelEnvironment";

import { Button } from "../../common/Button";
import { Modal } from "../../common/Modal";
import { ModalErrorBlock } from "./ConfirmModal";

// ---------------------------------------------------------------------------
// EnvironmentFormModal — attach/edit the per-channel execution workspace.
//
// The form reads as sections, not a wall of raw unit fields: RESOURCES and
// LIFECYCLE use preset chips in human units (1g/2g/4g, 1h/24h/∞) with a
// custom escape hatch, so the operator picks magnitudes instead of doing
// MB/seconds arithmetic. "No limit" states (∞ / never / forever) are chips
// like any other value — not checkbox+disabled-field pairs. Profile metadata
// collapses behind a per-card disclosure; base image / egress live under
// ADVANCED. The submit contract (ChannelEnvironmentFormValues) is unchanged.
// ---------------------------------------------------------------------------

export function EnvironmentFormModal({
  busy,
  errorMessage,
  initialValues,
  onClose,
  onSubmit,
  profiles = [],
  profilesError = null,
  profilesLoading = false,
  showProfileSelector = false,
  submitLabel,
  title,
}: {
  busy: boolean;
  errorMessage: string | null;
  initialValues: ChannelEnvironmentFormValues;
  onClose: () => void;
  onSubmit: (values: ChannelEnvironmentFormValues) => void;
  profiles?: readonly ChannelEnvironmentProfile[];
  profilesError?: string | null;
  profilesLoading?: boolean;
  showProfileSelector?: boolean;
  submitLabel: string;
  title: string;
}) {
  const [values, setValues] = useState(initialValues);
  const selectedProfile =
    profiles.find((profile) => profile.profileId === values.profileId) ??
    profiles.find((profile) => profile.profileId === "legacy-current-image") ??
    profiles[0] ??
    null;
  const caps = selectedProfile?.resources?.caps ?? null;

  const applyProfile = (profile: ChannelEnvironmentProfile) => {
    const defaults = profile.resources?.defaults;
    setValues({
      ...values,
      profileId: profile.profileId,
      ...(defaults
        ? {
            cpuCount: defaults.cpuMilli / 1000,
            ramMB: defaults.ramMb,
            storageMB: defaults.workspaceMb,
          }
        : {}),
    });
  };

  const footprint = useMemo(() => {
    const parts = [
      `${formatGb(values.ramMB)} ram`,
      `${values.cpuCount} cpu`,
      `${formatGb(values.storageMB)} disk`,
      `≤${values.maxConcurrentSpawns} spawns`,
      values.maxSpawnWallSeconds === null
        ? "no wall limit"
        : `${formatHours(values.maxSpawnWallSeconds / 3600)} wall`,
      values.idleStopAfterHours === null
        ? "never idles"
        : `idles after ${formatHours(values.idleStopAfterHours)}`,
    ];
    return parts.join(" · ");
  }, [values]);

  return (
    <Modal
      title={title}
      subtitle="Configure the shared per-channel execution workspace"
      widthClass="max-w-2xl"
      onClose={onClose}
      footer={
        <div className="flex items-center justify-between gap-3">
          <span className="min-w-0 truncate font-mono text-[9px] uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500">
            {footprint}
          </span>
          <div className="flex shrink-0 gap-2">
            <Button variant="secondary" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button
              size="sm"
              loading={busy}
              loadingText="Applying…"
              onClick={() => onSubmit(values)}
            >
              {submitLabel}
            </Button>
          </div>
        </div>
      }
    >
      {errorMessage && <ModalErrorBlock message={errorMessage} />}
      <div className="grid gap-5">
        {showProfileSelector && (
          <FormSection
            label="profile"
            meta={profilesLoading ? "loading…" : `${profiles.length} published`}
          >
            {profilesError && (
              <div className="border border-amber-500/40 bg-amber-500/5 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.12em] text-amber-700 dark:text-amber-300">
                Profiles unavailable: {profilesError}
              </div>
            )}
            <div className="grid max-h-64 gap-1.5 overflow-y-auto pr-1">
              {profiles.map((profile) => (
                <ProfileCard
                  key={profile.profileId}
                  profile={profile}
                  selected={profile.profileId === selectedProfile?.profileId}
                  onSelect={() => applyProfile(profile)}
                />
              ))}
              {!profilesLoading && profiles.length === 0 && (
                <div className="border border-dashed border-slate-300/70 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.12em] text-slate-500 dark:border-slate-700/70">
                  No published profiles are available.
                </div>
              )}
            </div>
          </FormSection>
        )}

        <FormSection label="resources">
          <PresetRow
            label="ram"
            options={[1024, 2048, 4096, 8192].map((mb) => ({
              disabled: caps !== null && mb > caps.maxRamMb,
              label: formatGb(mb),
              value: mb,
            }))}
            value={values.ramMB}
            customUnit="mb"
            customMax={caps?.maxRamMb}
            onChange={(ramMB) => {
              if (ramMB !== null) {
                setValues({ ...values, ramMB });
              }
            }}
          />
          <PresetRow
            label="cpu"
            options={[0.5, 1, 2, 4].map((count) => ({
              disabled: caps !== null && count > caps.maxCpuMilli / 1000,
              label: String(count),
              value: count,
            }))}
            value={values.cpuCount}
            customUnit="cores"
            customStep="0.5"
            customMax={caps ? caps.maxCpuMilli / 1000 : undefined}
            onChange={(cpuCount) => {
              if (cpuCount !== null) {
                setValues({ ...values, cpuCount });
              }
            }}
          />
          <PresetRow
            label="disk"
            options={[2048, 5120, 10_240, 20_480].map((mb) => ({
              disabled: caps !== null && mb > caps.maxWorkspaceMb,
              label: formatGb(mb),
              value: mb,
            }))}
            value={values.storageMB}
            customUnit="mb"
            customMax={caps?.maxWorkspaceMb}
            onChange={(storageMB) => {
              if (storageMB !== null) {
                setValues({ ...values, storageMB });
              }
            }}
          />
          <PresetRow
            label="spawns"
            options={[1, 3, 5, 10].map((count) => ({
              label: String(count),
              value: count,
            }))}
            value={values.maxConcurrentSpawns}
            customUnit="max"
            onChange={(maxConcurrentSpawns) => {
              if (maxConcurrentSpawns !== null) {
                setValues({ ...values, maxConcurrentSpawns });
              }
            }}
          />
        </FormSection>

        <FormSection label="lifecycle">
          <PresetRow
            label="spawn wall limit"
            options={[
              { label: "1h", value: 3600 },
              { label: "8h", value: 28_800 },
              { label: "24h", value: 86_400 },
              { label: "∞", value: null },
            ]}
            value={values.maxSpawnWallSeconds}
            customUnit="seconds"
            onChange={(maxSpawnWallSeconds) =>
              setValues({ ...values, maxSpawnWallSeconds })
            }
          />
          <PresetRow
            label="idle stop"
            options={[
              { label: "6h", value: 6 },
              { label: "24h", value: 24 },
              { label: "7d", value: 168 },
              { label: "never", value: null },
            ]}
            value={values.idleStopAfterHours}
            customUnit="hours"
            onChange={(idleStopAfterHours) =>
              setValues({ ...values, idleStopAfterHours })
            }
          />
          <PresetRow
            label="retention after kill"
            options={[
              { label: "7d", value: 7 },
              { label: "30d", value: 30 },
              { label: "90d", value: 90 },
              { label: "forever", value: null },
            ]}
            value={values.retentionDaysAfterChannelKill}
            customUnit="days"
            onChange={(retentionDaysAfterChannelKill) =>
              setValues({ ...values, retentionDaysAfterChannelKill })
            }
          />
        </FormSection>

        <FormSection label="actor tools">
          <div className="flex gap-2">
            <ToolsChoice
              active={values.actorProcessToolsEnabled}
              activeTone="emerald"
              title="Workspace + tools"
              sub="actors can spawn / exec"
              onClick={() =>
                setValues({ ...values, actorProcessToolsEnabled: true })
              }
            />
            <ToolsChoice
              active={!values.actorProcessToolsEnabled}
              activeTone="amber"
              title="Workspace only"
              sub="no process tools"
              onClick={() =>
                setValues({ ...values, actorProcessToolsEnabled: false })
              }
            />
          </div>
        </FormSection>

        <FormSection
          label="preview ports"
          meta={
            <button
              type="button"
              onClick={() =>
                setValues({
                  ...values,
                  previewPorts: [
                    ...values.previewPorts,
                    {
                      containerPort: 3000,
                      publicPort: 61_000 + values.previewPorts.length,
                    },
                  ],
                })
              }
              className="font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-700 hover:text-emerald-900 dark:text-emerald-300"
            >
              [ + port ]
            </button>
          }
        >
          {values.previewPorts.length === 0 ? (
            <div className="border border-dashed border-slate-300/70 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.12em] text-slate-500 dark:border-slate-700/70">
              No preview ports configured.
            </div>
          ) : (
            <div className="grid gap-1.5">
              {values.previewPorts.map((port, index) => (
                <PreviewPortRow
                  key={index}
                  port={port}
                  index={index}
                  values={values}
                  setValues={setValues}
                />
              ))}
            </div>
          )}
          <p className="font-mono text-[9px] uppercase tracking-[0.12em] text-slate-400 dark:text-slate-600">
            Bind dev servers to 0.0.0.0 · preview changes require restart
          </p>
        </FormSection>

        <AdvancedSection>
          <AdvancedLine
            label="base image"
            value={selectedProfile?.imageRef ?? "arti-process-sandbox:local"}
          />
          <AdvancedLine
            label="digest"
            value={selectedProfile?.imageDigest ?? "local / unpinned"}
          />
          <AdvancedLine label="egress policy" value="deny" />
        </AdvancedSection>
      </div>
    </Modal>
  );
}

// --- sections --------------------------------------------------------------

function FormSection({
  label,
  meta,
  children,
}: {
  label: string;
  meta?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="grid gap-2">
      <div className="flex items-center justify-between gap-2 border-b border-slate-200 pb-1 dark:border-slate-800">
        <span className="font-mono text-[9px] font-bold uppercase tracking-[0.25em] text-slate-500 dark:text-slate-400">
          [ {label} ]
        </span>
        {meta && (
          <span className="font-mono text-[9px] uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500">
            {meta}
          </span>
        )}
      </div>
      {children}
    </section>
  );
}

function AdvancedSection({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <section className="grid gap-2">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((previous) => !previous)}
        className="flex items-center gap-1.5 border-b border-slate-200 pb-1 text-left dark:border-slate-800"
      >
        <span
          aria-hidden
          className={`inline-block font-mono text-[9px] text-slate-400 transition-transform duration-150 ${
            open ? "rotate-90" : ""
          }`}
        >
          ▸
        </span>
        <span className="font-mono text-[9px] font-bold uppercase tracking-[0.25em] text-slate-500 dark:text-slate-400">
          [ advanced ]
        </span>
      </button>
      {open && <div className="grid gap-1">{children}</div>}
    </section>
  );
}

function AdvancedLine({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid gap-1 font-mono text-[10px] sm:grid-cols-[8rem_1fr]">
      <span className="uppercase tracking-[0.14em] text-slate-400 dark:text-slate-600">
        {label}
      </span>
      <span className="break-all text-slate-600 dark:text-slate-400">
        {value}
      </span>
    </div>
  );
}

// --- preset chips ------------------------------------------------------------

interface PresetOption {
  label: string;
  value: number | null;
  disabled?: boolean;
}

/** A labelled row of preset chips with a custom escape hatch. The current
 *  value highlights its chip; a value outside the presets opens the custom
 *  input automatically. `null` presets (∞ / never / forever) are ordinary
 *  chips — no checkbox+disabled-field pairs. */
function PresetRow({
  label,
  options,
  value,
  onChange,
  customUnit,
  customStep,
  customMax,
}: {
  label: string;
  options: PresetOption[];
  value: number | null;
  onChange: (value: number | null) => void;
  customUnit: string;
  customStep?: string;
  /** Profile cap for the custom input (server rejects above-cap anyway). */
  customMax?: number;
}) {
  const matchesPreset = options.some((option) => option.value === value);
  const [customOpen, setCustomOpen] = useState(!matchesPreset);
  const showCustom = customOpen || !matchesPreset;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="w-32 shrink-0 font-mono text-[9px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
        {label}
      </span>
      <div className="flex flex-wrap items-center gap-1">
        {options.map((option) => {
          const active = option.value === value;
          return (
            <button
              key={option.label}
              type="button"
              disabled={option.disabled}
              title={option.disabled ? "above the profile cap" : undefined}
              onClick={() => {
                setCustomOpen(false);
                onChange(option.value);
              }}
              className={`border px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-widest transition-colors disabled:cursor-not-allowed disabled:opacity-30 ${
                active
                  ? "border-emerald-400 bg-emerald-400/10 text-emerald-700 dark:text-emerald-300"
                  : "border-slate-300 text-slate-500 hover:border-emerald-400/50 hover:text-emerald-600 dark:border-slate-700 dark:text-slate-400 dark:hover:text-emerald-300"
              }`}
            >
              {option.label}
            </button>
          );
        })}
        <button
          type="button"
          onClick={() => setCustomOpen(true)}
          className={`border px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-widest transition-colors ${
            showCustom
              ? "border-sky-400 bg-sky-400/10 text-sky-700 dark:text-sky-300"
              : "border-slate-300 text-slate-400 hover:border-sky-400/50 hover:text-sky-600 dark:border-slate-700 dark:text-slate-500 dark:hover:text-sky-300"
          }`}
        >
          custom
        </button>
        {showCustom && (
          <label className="flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.12em] text-slate-400">
            <input
              type="number"
              min="1"
              max={customMax}
              step={customStep ?? "1"}
              value={value ?? ""}
              onChange={(event) => {
                const parsed = Number(event.currentTarget.value);
                if (!Number.isNaN(parsed) && event.currentTarget.value !== "") {
                  onChange(parsed);
                }
              }}
              className="w-24 border border-slate-300 bg-slate-50 px-2 py-1 text-[12px] normal-case tracking-normal text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
            />
            {customUnit}
          </label>
        )}
      </div>
    </div>
  );
}

// --- profile card ------------------------------------------------------------

/** Short chip copy for every admission reason. The chip is a badge, so it stays
 * terse; the server's `unavailableMessage` (rendered under details) carries the
 * explanation. Never print the raw snake_case reason. */
const UNAVAILABLE_REASON_CHIP_LABEL: Record<string, string> = {
  capacity_exhausted: "no capacity",
  image_unavailable: "image unavailable",
  missing_required_capability: "capability unavailable",
  no_eligible_node: "no eligible node",
};

function unavailableReasonChipLabel(reason: string | null): string {
  return reason === null
    ? "unavailable"
    : (UNAVAILABLE_REASON_CHIP_LABEL[reason] ?? "unavailable");
}

export function ProfileCard({
  profile,
  selected,
  onSelect,
}: {
  profile: ChannelEnvironmentProfile;
  selected: boolean;
  onSelect: () => void;
}) {
  const [detailsOpen, setDetailsOpen] = useState(false);
  return (
    <div
      className={`border transition ${
        selected
          ? "border-emerald-400 bg-emerald-400/10"
          : "border-slate-300 bg-slate-50/70 hover:border-emerald-400/70 dark:border-slate-700 dark:bg-slate-900/50"
      } ${profile.selectable ? "" : "opacity-50"}`}
    >
      <button
        type="button"
        disabled={!profile.selectable}
        onClick={onSelect}
        className="flex w-full flex-wrap items-center justify-between gap-2 px-3 py-2 text-left font-mono disabled:cursor-not-allowed"
      >
        <span className="min-w-0">
          <span
            className={`block truncate text-[11px] font-black uppercase tracking-[0.16em] ${
              selected
                ? "text-emerald-800 dark:text-emerald-200"
                : "text-slate-700 dark:text-slate-200"
            }`}
          >
            {profile.displayName}
          </span>
          <span className="mt-0.5 block truncate text-[9px] uppercase tracking-[0.12em] text-slate-500">
            {formatProfileDefaults(profile)}
          </span>
        </span>
        <span
          className={`shrink-0 border px-2 py-0.5 text-[8px] font-black uppercase tracking-[0.16em] ${
            profile.selectable
              ? "border-emerald-400/50 text-emerald-700 dark:text-emerald-300"
              : "border-amber-400/50 text-amber-700 dark:text-amber-300"
          }`}
        >
          {profile.selectable
            ? "available"
            : unavailableReasonChipLabel(profile.unavailableReason)}
        </span>
      </button>
      <button
        type="button"
        aria-expanded={detailsOpen}
        onClick={() => setDetailsOpen((previous) => !previous)}
        className="flex w-full items-center gap-1 border-t border-slate-200/70 px-3 py-1 text-left font-mono text-[8px] uppercase tracking-[0.2em] text-slate-400 hover:text-slate-600 dark:border-slate-800 dark:text-slate-600 dark:hover:text-slate-400"
      >
        <span
          aria-hidden
          className={`inline-block transition-transform duration-150 ${detailsOpen ? "rotate-90" : ""}`}
        >
          ▸
        </span>
        details
      </button>
      {detailsOpen && (
        <div className="grid gap-1 px-3 pb-2 font-mono text-[9px] uppercase tracking-[0.1em] text-slate-500">
          <ProfileDetailLine
            label="id"
            value={`${profile.profileId} · v${profile.version}`}
          />
          <ProfileDetailLine
            label="toolchains"
            value={formatProfileList(profile.toolchains)}
          />
          <ProfileDetailLine
            label="required"
            value={formatProfileList(profile.requiredCapabilities)}
          />
          <ProfileDetailLine label="caps" value={formatProfileCaps(profile)} />
          <ProfileDetailLine label="image" value={profile.imageRef} />
          <ProfileDetailLine
            label="digest"
            value={profile.imageDigest ?? "local / unpinned"}
          />
          <ProfileDetailLine label="readiness" value={profile.imageReadiness} />
          {profile.unavailableMessage && (
            <ProfileDetailLine
              label="availability"
              value={profile.unavailableMessage}
            />
          )}
        </div>
      )}
    </div>
  );
}

function ProfileDetailLine({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid gap-1 sm:grid-cols-[7rem_1fr]">
      <span className="text-slate-400">{label}</span>
      <span className="break-all text-slate-600 dark:text-slate-300">
        {value}
      </span>
    </div>
  );
}

// --- small controls ----------------------------------------------------------

function ToolsChoice({
  active,
  activeTone,
  title,
  sub,
  onClick,
}: {
  active: boolean;
  activeTone: "emerald" | "amber";
  title: string;
  sub: string;
  onClick: () => void;
}) {
  const activeClass =
    activeTone === "emerald"
      ? "border-emerald-400 bg-emerald-400/10 text-emerald-700 dark:text-emerald-300"
      : "border-amber-400 bg-amber-400/10 text-amber-700 dark:text-amber-300";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex-1 border px-3 py-2 text-left font-mono text-[11px] font-bold uppercase tracking-[0.16em] ${
        active
          ? activeClass
          : "border-slate-300 text-slate-500 dark:border-slate-700 dark:text-slate-400"
      }`}
    >
      {title}
      <span className="mt-0.5 block text-[8px] font-normal tracking-[0.08em] text-slate-500">
        {sub}
      </span>
    </button>
  );
}

function PortNumberInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
}) {
  return (
    <label className="font-mono text-[9px] uppercase tracking-[0.14em] text-slate-500">
      {label}
      <input
        type="number"
        min="1"
        value={value}
        onChange={(event) => onChange(Number(event.currentTarget.value))}
        className="mt-1 block w-24 border border-slate-300 bg-slate-50 px-2 py-1.5 font-mono text-[12px] normal-case tracking-normal text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
      />
    </label>
  );
}

// --- formatters ---------------------------------------------------------------

function formatGb(mb: number): string {
  const gb = mb / 1024;
  return `${Number.isInteger(gb) ? gb : gb.toFixed(1)}g`;
}

function formatHours(hours: number): string {
  if (hours >= 24 && hours % 24 === 0) {
    return `${hours / 24}d`;
  }
  return `${Number.isInteger(hours) ? hours : hours.toFixed(1)}h`;
}

function formatProfileList(values: readonly string[]): string {
  return values.length === 0 ? "none" : values.join(", ");
}

function formatProfileDefaults(profile: ChannelEnvironmentProfile): string {
  if (!profile.resources) {
    return "current workspace defaults";
  }
  const { defaults } = profile.resources;
  return [
    `${defaults.cpuMilli / 1000} cpu`,
    `${formatGb(defaults.ramMb)} ram`,
    `${formatGb(defaults.workspaceMb)} disk`,
    defaults.rootfsMb ? `${formatGb(defaults.rootfsMb)} rootfs` : null,
  ]
    .filter((part) => part !== null)
    .join(" · ");
}

function formatProfileCaps(profile: ChannelEnvironmentProfile): string {
  if (!profile.resources) {
    return "current workspace caps";
  }
  const { caps } = profile.resources;
  return [
    `${caps.maxCpuMilli / 1000} cpu`,
    `${formatGb(caps.maxRamMb)} ram`,
    `${formatGb(caps.maxWorkspaceMb)} disk`,
    caps.maxRootfsMb ? `${formatGb(caps.maxRootfsMb)} rootfs` : null,
  ]
    .filter((part) => part !== null)
    .join(" · ");
}

interface PreviewPortRowProps {
  port: ChannelEnvironmentFormValues["previewPorts"][number];
  index: number;
  values: ChannelEnvironmentFormValues;
  setValues: (values: ChannelEnvironmentFormValues) => void;
}

function PreviewPortRow({
  port,
  index,
  values,
  setValues,
}: PreviewPortRowProps) {
  return (
    <div
      key={index}
      className="flex flex-wrap items-end gap-2 border border-slate-300/70 p-2 dark:border-slate-700/70"
    >
      <PortNumberInput
        label="container"
        value={port.containerPort}
        onChange={(containerPort) =>
          setValues({
            ...values,
            previewPorts: values.previewPorts.map((candidate, i) =>
              i === index ? { ...candidate, containerPort } : candidate,
            ),
          })
        }
      />
      <span className="pb-2 font-mono text-[10px] text-slate-400">→</span>
      <PortNumberInput
        label="public"
        value={port.publicPort}
        onChange={(publicPort) =>
          setValues({
            ...values,
            previewPorts: values.previewPorts.map((candidate, i) =>
              i === index ? { ...candidate, publicPort } : candidate,
            ),
          })
        }
      />
      <label className="min-w-0 flex-1 font-mono text-[9px] uppercase tracking-[0.14em] text-slate-500">
        label
        <input
          value={port.label ?? ""}
          placeholder="web"
          onChange={(event) => {
            const label = event.target.value;
            setValues({
              ...values,
              previewPorts: values.previewPorts.map((candidate, i) =>
                i === index
                  ? {
                      ...candidate,
                      ...(label.trim()
                        ? { label: label.trim() }
                        : { label: undefined }),
                    }
                  : candidate,
              ),
            });
          }}
          className="mt-1 block w-full border border-slate-300 bg-white px-2 py-1.5 font-mono text-[12px] normal-case tracking-normal text-slate-900 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
        />
      </label>
      <button
        type="button"
        aria-label="Remove port"
        onClick={() =>
          setValues({
            ...values,
            previewPorts: values.previewPorts.filter(
              (_candidate, i) => i !== index,
            ),
          })
        }
        className="mb-0.5 border border-rose-400/50 px-2 py-1.5 font-mono text-[10px] font-bold text-rose-700 hover:bg-rose-500/10 dark:text-rose-300"
      >
        ×
      </button>
    </div>
  );
}
